import React, { useEffect, useRef } from 'react';
import loadable from '@wix/yoshi-flow-editor/loadable';
import {
  useEnvironment,
  useTranslation,
  WidgetProps,
} from '@wix/yoshi-flow-editor';
import { withSlotsPlaceholders } from '@wix/widget-plugins-ooi';
import { useSettings } from '@wix/tpa-settings/react';
import { SchedulingLocationViewModel } from '../../../service-page-view-model/scheduling-location-view-model/schedulingLocationViewModel';
import { SchedulingTimezoneViewModel } from '../../../service-page-view-model/shceduling-timezone-view-model/schedulingTimezoneViewModel';
import { SchedulingSectionViewModel } from '../../../service-page-view-model/scheduling-section-view-model/schedulingSectionViewModel';
import { ServicePageViewModel } from '../../../service-page-view-model/servicePageViewModel';
import { ISection, SectionTypes, SidebarPosition } from '../types';
import { BookButtonViewModelProvider } from './useBookButtonViewModel';
import { ServicePageActionsProvider } from './useServicePageActions';
import { classes, st } from './Widget.st.css';

import UserMessage from './UserMessage/UserMessage';
import TitleAndTagline from './TitleAndTagline';
import settingsParams from '../settingsParams';
import Description from './Description';
import Sidebar from './Sidebar/Sidebar';
import EmptyState from './EmptyState';
import Header from './Header/Header';
import Policy from './Policy/Policy';
import Details from './Details';
import Contact from './Contact';
import Plugin from './Plugin';
import Body from './Body/Body';
import { DimensionsProvider } from './useDimensions';
import { useWidgetSections } from './useWidgetSections';
import Section from './Section';
import { useAddVisibilityClass } from '../hooks/useAddVisibilityClass';
import { useVisibilityCheck } from '../hooks/useVisibilityCheck';
import { useSectionVisibilityCheck } from '../hooks/useSectionVisibilityCheck';
import { Dialog } from './Dialog/Dialog';
import { ServicePageActions } from '../actions/actions';

const Gallery = loadable(
  () => import(/* webpackChunkName: "Gallery" */ './Gallery/Gallery'),
);
const Scheduling = loadable(
  () => import(/* webpackChunkName: "Scheduling" */ './Scheduling/Scheduling'),
);

export interface ControllerProps {
  viewModel?: ServicePageViewModel;
  scheduleViewModel?: SchedulingSectionViewModel;
  locationViewModel?: SchedulingLocationViewModel;
  timezoneViewModel?: SchedulingTimezoneViewModel;
  actions: ServicePageActions;
  userMessage?: {
    shouldDisplayMessage: boolean;
    closeMessage: () => void;
  };
  isRedirectingToCalendar?: boolean;
}
const getSectionComponentByType = (
  section: ISection,
  viewModel: ServicePageViewModel,
  shouldSectionBookButtonBeVisible: (sectionType: SectionTypes) => boolean,
  schedule?: SchedulingSectionViewModel,
  locationViewModel?: SchedulingLocationViewModel,
  timezoneViewModel?: SchedulingTimezoneViewModel,
) => {
  switch (section.type) {
    case SectionTypes.TITLE_TAGLINE:
      return (
        <TitleAndTagline
          key={section.type}
          section={section}
          viewModel={viewModel.titleAndTagline}
        />
      );
    case SectionTypes.SCHEDULING:
      if (viewModel.showSchedulingSection) {
        return (
          <Section
            key={section.type}
            sectionType={SectionTypes.SCHEDULING}
            data-hook="scheduling-section"
            withBookButton={
              schedule?.isBookable &&
              shouldSectionBookButtonBeVisible(SectionTypes.SCHEDULING)
            }
          >
            <Scheduling
              key={section.type}
              schedule={schedule}
              locationViewModel={locationViewModel}
              timezoneViewModel={timezoneViewModel}
            />
          </Section>
        );
      }
      break;
    case SectionTypes.POLICY:
      return <Policy key={section.type} viewModel={viewModel.policySection} />;
    case SectionTypes.DETAILS:
      return (
        <Details
          key={section.type}
          section={section}
          viewModel={viewModel.detailsSection}
        />
      );
    case SectionTypes.DESCRIPTION:
      return (
        <Description
          key={section.type}
          section={section}
          viewModel={viewModel.descriptionSection}
        />
      );
    case SectionTypes.CONTACT:
      return (
        <Contact
          key={section.type}
          section={section}
          viewModel={viewModel.contactSection}
        />
      );
    case SectionTypes.GALLERY:
      return viewModel.gallerySection.items.length ? (
        <Section sectionType={SectionTypes.GALLERY} key={section.type}>
          <Gallery viewModel={viewModel.gallerySection} />
        </Section>
      ) : null;
    case SectionTypes.PLUGIN:
      return <Plugin key={section.type} />;
    default:
      return null;
  }
};

export const Widget: React.FC<WidgetProps<ControllerProps>> = ({
  viewModel,
  scheduleViewModel,
  locationViewModel,
  timezoneViewModel,
  userMessage,
  isRedirectingToCalendar,
  actions,
}) => {
  const { t } = useTranslation();
  const { isMobile, isRTL } = useEnvironment();
  const settings = useSettings();
  const widgetRef = useRef<HTMLDivElement>(null);
  const sections = useWidgetSections();
  const { isCssPerBreakpoint } = useEnvironment();
  const { addVisibilityClass } = useAddVisibilityClass();
  const { shouldBeVisible } = useVisibilityCheck();
  const { shouldSectionBookButtonBeVisible, shouldSectionBeVisible } =
    useSectionVisibilityCheck();

  const getSectionByType = (sectionType: SectionTypes): ISection => {
    return sections.find(({ type }) => type === sectionType) as ISection;
  };

  const isSectionVisible = (section: ISection): boolean => {
    return isCssPerBreakpoint
      ? shouldSectionBeVisible(section.type)
      : shouldSectionBeVisible(section.type) &&
          (!shouldBeVisible('sidebarVisibility') ||
            section.type !== settings.get(settingsParams.sidebarSection));
  };

  const shouldShowOnlyImage =
    shouldBeVisible('headerImageVisibility') &&
    !shouldBeVisible('headerBookButtonVisibility') &&
    !shouldBeVisible('headerTitleVisibility');
  const isServiceWithoutImage = !viewModel?.header?.image;
  const shouldShowHeader =
    shouldBeVisible('headerVisibility') &&
    !(shouldShowOnlyImage && isServiceWithoutImage);

  const renderBody = (widgetViewModel) => (
    <Body viewModel={widgetViewModel.body}>
      {sections.map((section) =>
        isSectionVisible(section)
          ? getSectionComponentByType(
              section,
              widgetViewModel,
              shouldSectionBookButtonBeVisible,
              scheduleViewModel,
              locationViewModel,
              timezoneViewModel,
            )
          : null,
      )}
    </Body>
  );

  const renderSideBar = (widgetViewModel) =>
    shouldBeVisible('sidebarVisibility') ? (
      <Sidebar
        sectionType={settings.get(settingsParams.sidebarSection)}
        className={addVisibilityClass(
          '',
          classes.sidebarVisibility,
          'sidebarVisibility',
        )}
      >
        {getSectionComponentByType(
          getSectionByType(settings.get(settingsParams.sidebarSection)),
          widgetViewModel,
          shouldSectionBookButtonBeVisible,
          scheduleViewModel,
          locationViewModel,
          timezoneViewModel,
        )}
      </Sidebar>
    ) : null;

  useEffect(() => {
    actions?.reportPageLoaded?.({
      emptyState: !viewModel,
      message_to_user: viewModel?.body.messageType,
      platform: isMobile ? 'mobile' : '',
      containers: viewModel
        ? `body${shouldShowHeader ? ',header' : ''}${
            shouldBeVisible('sidebarVisibility') ? ',sidebar' : ''
          }`
        : '',
      sections: viewModel ? JSON.stringify(sections) : '',
    });
  }, [actions.reportPageLoaded]);

  const isSideBarOnTheRight = () =>
    settings.get(settingsParams.sidebarPosition) === SidebarPosition.RIGHT;
  const isNoHeaderAndTitleInSideBar = () => {
    const isSideBarVisible = shouldBeVisible('sidebarVisibility');
    const isSideBarSectionIsTitleAndTagline =
      settings.get(settingsParams.sidebarSection) ===
      SectionTypes.TITLE_TAGLINE;
    const isSideBarTitleVisible = shouldBeVisible('serviceTitleVisibility');
    return (
      !shouldShowHeader &&
      isSideBarVisible &&
      isSideBarSectionIsTitleAndTagline &&
      isSideBarTitleVisible
    );
  };
  const inverseRowBySideBarPositionAndNoHeaderLogic =
    (isNoHeaderAndTitleInSideBar() && isSideBarOnTheRight()) ||
    (!isNoHeaderAndTitleInSideBar() && !isSideBarOnTheRight());

  return (
    <ServicePageActionsProvider value={actions}>
      <BookButtonViewModelProvider value={viewModel?.button}>
        <DimensionsProvider widgetRef={widgetRef}>
          <div
            className={st(classes.root, {
              sidebarPosition: settings.get(settingsParams.sidebarPosition),
              isMobile,
              isNoHeaderAndTitleInSideBar:
                inverseRowBySideBarPositionAndNoHeaderLogic,
              isNavigating: !!viewModel?.button?.navigationInitiatedBy,
            })}
            ref={widgetRef}
          >
            {viewModel ? (
              <>
                <Dialog viewModel={viewModel.dialogViewModel} />
                <UserMessage
                  isOpen={userMessage?.shouldDisplayMessage || false}
                  onRequestClose={userMessage?.closeMessage || (() => {})}
                  isMobile={isMobile}
                  message={t('app.widget.uou-messages.non.premium.message')}
                  okLabel={t('app.widget.uou-messages.non.premium.approve')}
                  isRTL={isRTL}
                />
                <div
                  className={classes.root}
                  data-hook="booking-service-page-wrapper"
                >
                  {shouldShowHeader && (
                    <Header
                      viewModel={viewModel.header}
                      className={addVisibilityClass(
                        '',
                        classes.headerVisibility,
                        'headerVisibility',
                      )}
                    />
                  )}
                  <div className={classes.dynamicWrapper}>
                    {isNoHeaderAndTitleInSideBar() ? (
                      <>
                        {renderSideBar(viewModel)}
                        {renderBody(viewModel)}
                      </>
                    ) : (
                      <>
                        {renderBody(viewModel)}
                        {renderSideBar(viewModel)}
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <EmptyState isRedirectingToCalendar={isRedirectingToCalendar} />
            )}
          </div>
        </DimensionsProvider>
      </BookButtonViewModelProvider>
    </ServicePageActionsProvider>
  );
};

export default withSlotsPlaceholders(Widget);
